import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { TESTBALK_TOP } from '../../utils/testbalk';

// ── Constants ───────────────────────────────────────────────────────────────
const GRID_W = 64;
const GRID_H = 64;
const TILE_W = 64;   // isometric tile width
const TILE_H = 32;   // isometric tile height
const MOVE_SPEED = 4; // tiles per second
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;

// ── Greenery assets ─────────────────────────────────────────────────────────
const GREENERY_DEFS = [
  { bg: '/assets/lab/Greenery_1x1_BG.svg', fg: '/assets/lab/Greenery_1x1_FG.svg', tilesX: 1, tilesY: 1, w: 32, h: 22.26 },
  { bg: '/assets/lab/Greenery_1x2_BG.svg', fg: '/assets/lab/Greenery_1x2_FG.svg', tilesX: 2, tilesY: 1, w: 48, h: 28.91 },
  { bg: '/assets/lab/Greenery_2x3_BG.svg', fg: '/assets/lab/Greenery_2x3_FG.svg', tilesX: 3, tilesY: 2, w: 80, h: 50.85 },
  { bg: '/assets/lab/Factory_4x4_BG.svg', fg: '/assets/lab/Factory_4x4_FG.svg', tilesX: 4, tilesY: 4, w: 127.5, h: 117.34 },
];

function preRenderSVG(src, w, h, renderScale) {
  const offscreen = document.createElement('canvas');
  offscreen.width = Math.ceil(w * renderScale);
  offscreen.height = Math.ceil(h * renderScale);
  const img = new Image();
  img.onload = () => {
    const ctx = offscreen.getContext('2d');
    ctx.drawImage(img, 0, 0, offscreen.width, offscreen.height);
  };
  img.src = src;
  return { offscreen, img };
}

function loadGreeneryImages() {
  const RENDER_SCALE = 4;
  return GREENERY_DEFS.map(def => {
    const footprintW = (def.tilesX + def.tilesY) * TILE_W / 2;
    const scale = footprintW / def.w;
    const drawW = footprintW;
    const drawH = def.h * scale;

    const bgLayer = preRenderSVG(def.bg, drawW, drawH, RENDER_SCALE);
    const fgLayer = preRenderSVG(def.fg, drawW, drawH, RENDER_SCALE);

    return { ...def, bgLayer, fgLayer, drawW, drawH };
  });
}

// ── Color palette ───────────────────────────────────────────────────────────
const COLORS = {
  bg: '#ffffff',
  tile: '#ffffff',
  tileStroke: '#5988A3',
  tileHover: '#e8f1f5',
  charBody: '#7c3aed',
  charHead: '#a78bfa',
  charShadow: 'rgba(0,0,0,0.15)',
  path: 'rgba(89,136,163,0.12)',
  ui: '#2d3335',
  uiDim: '#94a3b8',
};

// ── Box assets ──────────────────────────────────────────────────────────────
const BOX_SRCS = [
  '/assets/lab/Box_Blue_1.svg', '/assets/lab/Box_Blue_2.svg',
  '/assets/lab/Box_Orange_1.svg', '/assets/lab/Box_Orange_2.svg',
  '/assets/lab/Box_Purple_1.svg', '/assets/lab/Box_Purple_2.svg',
];
const BOX_SVG_W = 13.93;
const BOX_SVG_H = 13.1;
const BOX_DRAW_W = 28; // screen size on tile
const BOX_DRAW_H = BOX_SVG_H / BOX_SVG_W * BOX_DRAW_W;

function loadBoxImages() {
  const RENDER_SCALE = 4;
  return BOX_SRCS.map(src => {
    const r = preRenderSVG(src, BOX_DRAW_W, BOX_DRAW_H, RENDER_SCALE);
    return r;
  });
}

// ── Info points ─────────────────────────────────────────────────────────────
const INFO_SVG_W = 21.89;
const INFO_SVG_H = 26.4;
const INFO_DRAW_W = 36;
const INFO_DRAW_H = INFO_SVG_H / INFO_SVG_W * INFO_DRAW_W;

const INFO_POINTS = [
  {
    x: 33, y: 37,
    text: 'Ahlstrom produceert duurzame filtratieoplossingen voor industriële toepassingen wereldwijd.',
  },
];

function loadInfoImage() {
  return preRenderSVG('/assets/lab/Info_1_1x1.svg', INFO_DRAW_W, INFO_DRAW_H, 4);
}

// ── Isometric helpers ───────────────────────────────────────────────────────
function tileToScreen(tx, ty) {
  return {
    x: (tx - ty) * (TILE_W / 2),
    y: (tx + ty) * (TILE_H / 2),
  };
}

function screenToTile(sx, sy) {
  const tx = (sx / (TILE_W / 2) + sy / (TILE_H / 2)) / 2;
  const ty = (sy / (TILE_H / 2) - sx / (TILE_W / 2)) / 2;
  return { tx: Math.floor(tx), ty: Math.floor(ty) };
}

// ── A* pathfinding ──────────────────────────────────────────────────────────
function findPath(sx, sy, ex, ey, blocked) {
  if (ex < 0 || ey < 0 || ex >= GRID_W || ey >= GRID_H) return [];
  if (blocked.has(`${ex},${ey}`)) return [];

  const open = [{ x: sx, y: sy, g: 0, h: 0, f: 0, parent: null }];
  const closed = new Set();
  const key = (x, y) => `${x},${y}`;

  const heuristic = (x, y) => Math.abs(x - ex) + Math.abs(y - ey);
  open[0].h = heuristic(sx, sy);
  open[0].f = open[0].h;

  const dirs = [[1,0],[-1,0],[0,1],[0,-1],[1,1],[-1,-1],[1,-1],[-1,1]];

  let iterations = 0;
  while (open.length > 0 && iterations < 5000) {
    iterations++;
    open.sort((a, b) => a.f - b.f);
    const current = open.shift();
    const ck = key(current.x, current.y);

    if (current.x === ex && current.y === ey) {
      const path = [];
      let node = current;
      while (node) { path.unshift({ x: node.x, y: node.y }); node = node.parent; }
      return path;
    }

    closed.add(ck);

    for (const [dx, dy] of dirs) {
      const nx = current.x + dx;
      const ny = current.y + dy;
      if (nx < 0 || ny < 0 || nx >= GRID_W || ny >= GRID_H) continue;
      const nk = key(nx, ny);
      if (closed.has(nk) || blocked.has(nk)) continue;

      const isDiag = dx !== 0 && dy !== 0;
      const ng = current.g + (isDiag ? 1.414 : 1);
      const existing = open.find(n => n.x === nx && n.y === ny);
      if (existing && existing.g <= ng) continue;

      if (existing) {
        existing.g = ng;
        existing.h = heuristic(nx, ny);
        existing.f = ng + existing.h;
        existing.parent = current;
      } else {
        const h = heuristic(nx, ny);
        open.push({ x: nx, y: ny, g: ng, h, f: ng + h, parent: current });
      }
    }
  }
  return [];
}

// ── Fixed world layout (seeded) ─────────────────────────────────────────────
function seededRandom(seed) {
  let s = seed;
  return () => { s = (s * 16807 + 0) % 2147483647; return (s - 1) / 2147483646; };
}

function buildFixedWorld(greeneryAssets, boxImages) {
  const rand = seededRandom(42);
  const occupied = new Set();
  const items = [];

  const greeneryOnly = greeneryAssets.filter(a => !a.bg.includes('Factory'));
  const factoryAsset = greeneryAssets.find(a => a.bg.includes('Factory'));

  // Place 1 factory in the center
  const factoryPos = { x: 30, y: 30 };
  if (factoryAsset) {
    for (let dx = 0; dx < factoryAsset.tilesX; dx++) {
      for (let dy = 0; dy < factoryAsset.tilesY; dy++) {
        occupied.add(`${factoryPos.x + dx},${factoryPos.y + dy}`);
      }
    }
    items.push({ x: factoryPos.x, y: factoryPos.y, asset: factoryAsset, depth: factoryPos.x + factoryPos.y + factoryAsset.tilesX + factoryAsset.tilesY - 2 });
  }

  // Place 40 greenery with seeded random
  let placed = 0;
  let attempts = 0;
  while (placed < 40 && attempts < 400) {
    attempts++;
    const asset = greeneryOnly[Math.floor(rand() * greeneryOnly.length)];
    const x = Math.floor(rand() * (GRID_W - asset.tilesX));
    const y = Math.floor(rand() * (GRID_H - asset.tilesY));
    let overlap = false;
    for (let dx = 0; dx < asset.tilesX && !overlap; dx++) {
      for (let dy = 0; dy < asset.tilesY && !overlap; dy++) {
        if (occupied.has(`${x + dx},${y + dy}`)) overlap = true;
      }
    }
    if (overlap) continue;
    for (let dx = 0; dx < asset.tilesX; dx++) {
      for (let dy = 0; dy < asset.tilesY; dy++) {
        occupied.add(`${x + dx},${y + dy}`);
      }
    }
    items.push({ x, y, asset, depth: x + y + asset.tilesX + asset.tilesY - 2 });
    placed++;
  }

  items.sort((a, b) => a.depth - b.depth);

  // Place 30 boxes with seeded random
  const boxes = new Map();
  let boxPlaced = 0;
  attempts = 0;
  while (boxPlaced < 30 && attempts < 300) {
    attempts++;
    const x = Math.floor(rand() * GRID_W);
    const y = Math.floor(rand() * GRID_H);
    const k = `${x},${y}`;
    if (!occupied.has(k) && !boxes.has(k)) {
      boxes.set(k, { x, y, variant: Math.floor(rand() * boxImages.length) });
      boxPlaced++;
    }
  }

  // Drop zone: tiles directly in front of the factory (1 row past front edge)
  const dropZone = new Set();
  if (factoryAsset) {
    const fx = factoryPos.x, fy = factoryPos.y;
    const tx = factoryAsset.tilesX, ty = factoryAsset.tilesY;
    // Front edge + 1 row: tiles where x+y = fx+fy+tx+ty-1 (just past front corner)
    for (let dx = -1; dx <= tx; dx++) {
      for (let dy = -1; dy <= ty; dy++) {
        const px = fx + dx, py = fy + dy;
        if (px >= 0 && py >= 0 && px < GRID_W && py < GRID_H) {
          if (px + py === fx + fy + tx + ty - 1 || px + py === fx + fy + tx + ty - 2) {
            dropZone.add(`${px},${py}`);
          }
        }
      }
    }
  }

  return { greeneryItems: items, boxes, dropZone };
}

// ── Main component ──────────────────────────────────────────────────────────
export default function AhlstromInteractive() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const stateRef = useRef({
    char: { x: 31, y: 35 },
    target: { x: 31, y: 35 },
    path: [],
    pathIndex: 0,
    moving: false,
    moveProgress: 0,
    fromTile: { x: 31, y: 35 },

    camera: { x: 0, y: 0 },
    zoom: 1,
    hover: null,
    boxes: new Map(),
    boxImages: [],
    pickupAnims: [],
    deliverAnims: [],
    factoryPulse: 0,   // countdown for factory glow effect
    dropZone: new Set(),
    carried: 0,
    delivered: 0,
    infoImage: null,
    hoveredInfo: null,  // index into INFO_POINTS or null
    blocked: new Set(),
    greeneryAssets: [],
    greeneryItems: [],
    time: 0,

    // Drag state
    dragging: false,
    dragStart: { x: 0, y: 0 },
    cameraStart: { x: 0, y: 0 },
    didDrag: false,
  });
  const [started, setStarted] = useState(false);
  const startedRef = useRef(false);
  const [carried, setCarried] = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [debug, setDebug] = useState(false);
  const debugRef = useRef(false);
  const [showUI, setShowUI] = useState(true);

  // ── Init world (fixed layout) ───────────────────────────────────────────
  useEffect(() => {
    const s = stateRef.current;
    s.greeneryAssets = loadGreeneryImages();
    s.boxImages = loadBoxImages();
    const world = buildFixedWorld(s.greeneryAssets, s.boxImages);
    s.greeneryItems = world.greeneryItems;
    s.boxes = world.boxes;
    s.dropZone = world.dropZone;
    s.infoImage = loadInfoImage();
    const charScreen = tileToScreen(s.char.x, s.char.y);
    s.camera.x = charScreen.x;
    s.camera.y = charScreen.y;
  }, []);

  // ── Mouse → world coordinate ────────────────────────────────────────────
  const getWorldPos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const s = stateRef.current;
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const worldX = (mx - rect.width / 2) / s.zoom + s.camera.x;
    const worldY = (my - rect.height / 2) / s.zoom + s.camera.y;
    return { worldX, worldY };
  }, []);

  const getMouseTile = useCallback((e) => {
    const pos = getWorldPos(e);
    if (!pos) return null;
    const { tx, ty } = screenToTile(pos.worldX, pos.worldY);
    if (tx >= 0 && ty >= 0 && tx < GRID_W && ty < GRID_H) return { tx, ty };
    return null;
  }, [getWorldPos]);

  // ── Debug toggle (D key) ─────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'd' || e.key === 'D') { debugRef.current = !debugRef.current; setDebug(debugRef.current); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Click (only if not dragging) ────────────────────────────────────────
  const handleClick = useCallback((e) => {
    const s = stateRef.current;
    if (!startedRef.current) return;
    if (s.didDrag) return;
    const tile = getMouseTile(e);
    if (!tile) return;
    const charTile = { x: Math.round(s.char.x), y: Math.round(s.char.y) };
    const path = findPath(charTile.x, charTile.y, tile.tx, tile.ty, s.blocked);
    if (path.length > 1) {
      s.path = path;
      s.pathIndex = 1;
      s.moving = true;
      s.moveProgress = 0;
      s.fromTile = { x: charTile.x, y: charTile.y };
      s.target = { x: path[1].x, y: path[1].y };
    }
  }, [getMouseTile]);

  // ── Drag to pan ─────────────────────────────────────────────────────────
  const handleMouseDown = useCallback((e) => {
    const s = stateRef.current;
    s.dragging = true;
    s.didDrag = false;
    s.dragStart = { x: e.clientX, y: e.clientY };
    s.cameraStart = { x: s.camera.x, y: s.camera.y };
  }, []);

  const handleMouseMove = useCallback((e) => {
    const s = stateRef.current;
    if (s.dragging) {
      const dx = e.clientX - s.dragStart.x;
      const dy = e.clientY - s.dragStart.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) s.didDrag = true;
      s.camera.x = s.cameraStart.x - dx / s.zoom;
      s.camera.y = s.cameraStart.y - dy / s.zoom;
    }
    const tile = getMouseTile(e);
    s.hover = tile;

    // Check info point hover (1 tile margin around)
    s.hoveredInfo = null;
    if (tile) {
      for (let i = 0; i < INFO_POINTS.length; i++) {
        const dx = Math.abs(tile.tx - INFO_POINTS[i].x);
        const dy = Math.abs(tile.ty - INFO_POINTS[i].y);
        if (dx <= 1 && dy <= 1) {
          s.hoveredInfo = i;
          break;
        }
      }
    }
  }, [getMouseTile]);

  const handleMouseUp = useCallback(() => {
    stateRef.current.dragging = false;
  }, []);

  const handleMouseLeave = useCallback(() => {
    stateRef.current.hover = null;
    stateRef.current.dragging = false;
  }, []);

  // ── Scroll to zoom ─────────────────────────────────────────────────────
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    // World position under cursor before zoom
    const worldXBefore = (mx - rect.width / 2) / s.zoom + s.camera.x;
    const worldYBefore = (my - rect.height / 2) / s.zoom + s.camera.y;

    // Apply zoom
    const direction = e.deltaY < 0 ? 1 : -1;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, s.zoom * (1 + direction * ZOOM_STEP)));
    s.zoom = newZoom;

    // Adjust camera so cursor stays over same world point
    const worldXAfter = (mx - rect.width / 2) / s.zoom + s.camera.x;
    const worldYAfter = (my - rect.height / 2) / s.zoom + s.camera.y;
    s.camera.x += worldXBefore - worldXAfter;
    s.camera.y += worldYBefore - worldYAfter;
  }, []);

  // ── Attach wheel listener (non-passive) ─────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // ── Draw character helper ────────────────────────────────────────────────
  const drawCharacter = useCallback((ctx, s) => {
    const { x: cx, y: cy } = tileToScreen(s.char.x, s.char.y);

    // Shadow
    ctx.beginPath();
    ctx.ellipse(cx, cy, 12, 6, 0, 0, Math.PI * 2);
    ctx.fillStyle = COLORS.charShadow;
    ctx.fill();

    // Body
    const bodyH = TILE_H * 1.6;
    const bodyW = 14;
    ctx.fillStyle = COLORS.charBody;
    ctx.beginPath();
    ctx.roundRect(cx - bodyW / 2, cy - bodyH, bodyW, bodyH, 3);
    ctx.fill();

    // Head
    ctx.beginPath();
    ctx.arc(cx, cy - bodyH - 6, 8, 0, Math.PI * 2);
    ctx.fillStyle = COLORS.charHead;
    ctx.fill();

    // Carried bubble
    if (s.carried > 0) {
      const bx = cx + 12;
      const by = cy - bodyH - 22;
      const text = `${s.carried}`;
      ctx.font = 'bold 10px Inter, system-ui, sans-serif';
      const tw = ctx.measureText(text).width;
      const pw = Math.max(tw + 10, 18);
      const ph = 16;

      // Bubble
      ctx.fillStyle = '#7c3aed';
      ctx.beginPath();
      ctx.roundRect(bx - pw / 2, by - ph / 2, pw, ph, 8);
      ctx.fill();

      // Triangle pointer
      ctx.beginPath();
      ctx.moveTo(bx - 3, by + ph / 2);
      ctx.lineTo(bx + 3, by + ph / 2);
      ctx.lineTo(bx, by + ph / 2 + 4);
      ctx.closePath();
      ctx.fill();

      // Text
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, bx, by);
      ctx.textAlign = 'start';
      ctx.textBaseline = 'alphabetic';
    }
  }, []);

  // ── Game loop ───────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    let animId;
    let lastTime = performance.now();

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      const w = canvas.parentElement.clientWidth;
      const h = canvas.parentElement.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
    };
    resize();
    window.addEventListener('resize', resize);

    const loop = (now) => {
      const dt = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;
      const s = stateRef.current;
      s.time = now / 1000;

      // ── Update character movement ───────────────────────────────────
      if (s.moving) {
        s.moveProgress += MOVE_SPEED * dt;
        const dx = s.target.x - s.fromTile.x;
        const dy = s.target.y - s.fromTile.y;
        const isDiag = dx !== 0 && dy !== 0;
        const stepDist = isDiag ? 1.414 : 1;

        if (s.moveProgress >= stepDist) {
          s.char.x = s.target.x;
          s.char.y = s.target.y;

          const ck = `${s.target.x},${s.target.y}`;
          // Pick up box
          if (s.boxes.has(ck)) {
            const box = s.boxes.get(ck);
            s.pickupAnims.push({ x: box.x, y: box.y, variant: box.variant, t: 0, duration: 0.4 });
            s.boxes.delete(ck);
            s.carried++;
            setCarried(s.carried);
          }
          // Drop boxes at factory
          if (s.dropZone.has(ck) && s.carried > 0) {
            const count = s.carried;
            // Spawn particles from character position toward factory
            const particles = [];
            for (let i = 0; i < Math.min(count * 3, 15); i++) {
              particles.push({
                dx: (Math.random() - 0.5) * 60,
                dy: -Math.random() * 80 - 20,
                size: 3 + Math.random() * 4,
                delay: Math.random() * 0.15,
                color: ['#7c3aed', '#10b981', '#f59e0b', '#3b82f6'][Math.floor(Math.random() * 4)],
              });
            }
            s.deliverAnims.push({ x: s.char.x, y: s.char.y, count, t: 0, duration: 0.8, particles });
            s.factoryPulse = 0.6;
            s.delivered += count;
            s.carried = 0;
            setCarried(0);
            setDelivered(s.delivered);
          }

          s.pathIndex++;
          if (s.pathIndex < s.path.length) {
            s.fromTile = { x: s.target.x, y: s.target.y };
            s.target = { x: s.path[s.pathIndex].x, y: s.path[s.pathIndex].y };
            s.moveProgress = 0;
          } else {
            s.moving = false;
            s.path = [];
          }
        } else {
          const t = s.moveProgress / stepDist;
          s.char.x = s.fromTile.x + (s.target.x - s.fromTile.x) * t;
          s.char.y = s.fromTile.y + (s.target.y - s.fromTile.y) * t;
        }
      }

      // ── Render ──────────────────────────────────────────────────────
      const W = canvas.width / dpr;
      const H = canvas.height / dpr;
      const zoom = s.zoom;

      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = COLORS.bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();

      ctx.save();
      ctx.translate(W / 2, H / 2);
      ctx.scale(zoom, zoom);
      ctx.translate(-s.camera.x, -s.camera.y);

      // Visible tile range (cull off-screen)
      const viewW = W / zoom;
      const viewH = H / zoom;
      const margin = 4;
      const centerTile = screenToTile(s.camera.x, s.camera.y);
      const visRange = Math.ceil(Math.max(viewW, viewH) / TILE_H) + margin;
      const minTx = Math.max(0, centerTile.tx - visRange);
      const maxTx = Math.min(GRID_W - 1, centerTile.tx + visRange);
      const minTy = Math.max(0, centerTile.ty - visRange);
      const maxTy = Math.min(GRID_H - 1, centerTile.ty + visRange);

      const pathSet = new Set(s.path.slice(s.pathIndex).map(p => `${p.x},${p.y}`));

      // ── Draw tiles ────────────────────────────────────────────────
      for (let ty = minTy; ty <= maxTy; ty++) {
        for (let tx = minTx; tx <= maxTx; tx++) {
          const { x: sx, y: sy } = tileToScreen(tx, ty);

          const isHover = s.hover && s.hover.tx === tx && s.hover.ty === ty;
          const isPath = pathSet.has(`${tx},${ty}`);

          ctx.beginPath();
          ctx.moveTo(sx, sy - TILE_H / 2);
          ctx.lineTo(sx + TILE_W / 2, sy);
          ctx.lineTo(sx, sy + TILE_H / 2);
          ctx.lineTo(sx - TILE_W / 2, sy);
          ctx.closePath();

          ctx.fillStyle = isHover ? COLORS.tileHover : isPath ? COLORS.path : COLORS.tile;
          ctx.fill();
          ctx.strokeStyle = COLORS.tileStroke;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }

      // ── Draw boxes (on ground, before depth-sorted objects) ────────
      s.boxes.forEach((box) => {
        if (box.x < minTx || box.x > maxTx || box.y < minTy || box.y > maxTy) return;
        const { x: sx, y: sy } = tileToScreen(box.x, box.y);
        const bi = s.boxImages[box.variant];
        if (!bi || !bi.img.complete) return;
        ctx.drawImage(bi.offscreen, sx - BOX_DRAW_W / 2, sy - BOX_DRAW_H + TILE_H / 4, BOX_DRAW_W, BOX_DRAW_H);
      });

      // ── Draw pickup animations ────────────────────────────────────
      s.pickupAnims = s.pickupAnims.filter(a => {
        a.t += dt;
        if (a.t >= a.duration) return false;
        const progress = a.t / a.duration;
        const ease = 1 - (1 - progress) * (1 - progress); // ease-out quad
        const { x: sx, y: sy } = tileToScreen(a.x, a.y);
        const bi = s.boxImages[a.variant];
        if (!bi || !bi.img.complete) return false;
        const offsetY = -30 * ease;
        const scale = 1 - 0.3 * ease;
        const alpha = 1 - ease;
        ctx.globalAlpha = alpha;
        const w = BOX_DRAW_W * scale;
        const h = BOX_DRAW_H * scale;
        ctx.drawImage(bi.offscreen, sx - w / 2, sy - h + TILE_H / 4 + offsetY, w, h);
        ctx.globalAlpha = 1;
        return true;
      });

      // ── Depth-sorted render: greenery BG/FG + character ────────────
      const charDepth = s.char.x + s.char.y;
      const fgOverlays = []; // FG layers that go on top of character

      // Pass 1: all BG layers + FG for items fully behind character
      for (const item of s.greeneryItems) {
        if (item.x + item.asset.tilesX < minTx || item.x > maxTx ||
            item.y + item.asset.tilesY < minTy || item.y > maxTy) continue;

        const { bgLayer, fgLayer, drawW, drawH, tilesX, tilesY } = item.asset;
        const cx = item.x + (tilesX - 1) / 2;
        const cy = item.y + (tilesY - 1) / 2;
        const center = tileToScreen(cx, cy);
        const ax = center.x;
        const ay = center.y + TILE_H * (tilesX + tilesY) / 4;

        // BG always behind character
        if (bgLayer.img.complete) ctx.drawImage(bgLayer.offscreen, ax - drawW / 2, ay - drawH, drawW, drawH);

        // FG overlays when character is within or behind the footprint (both axes)
        const charTx = Math.round(s.char.x);
        const charTy = Math.round(s.char.y);
        if (charTx < item.x + tilesX && charTy < item.y + tilesY) {
          // Character is behind object: FG goes on top later
          if (fgLayer.img.complete) fgOverlays.push({ offscreen: fgLayer.offscreen, ax, ay, drawW, drawH });
        } else {
          // Character is on or past footprint: FG also behind character
          if (fgLayer.img.complete) ctx.drawImage(fgLayer.offscreen, ax - drawW / 2, ay - drawH, drawW, drawH);
        }
      }

      // Pass 2: character
      drawCharacter(ctx, s);

      // Pass 3: FG overlays (only objects the character is behind)
      for (const { offscreen, ax, ay, drawW, drawH } of fgOverlays) {
        ctx.drawImage(offscreen, ax - drawW / 2, ay - drawH, drawW, drawH);
      }

      // ── Factory pulse effect (brighten the image) ─────────────────
      if (s.factoryPulse > 0) {
        s.factoryPulse = Math.max(0, s.factoryPulse - dt);
        const pulse = s.factoryPulse / 0.6;
        for (const item of s.greeneryItems) {
          if (!item.asset.bg.includes('Factory')) continue;
          const { bgLayer, fgLayer, drawW, drawH, tilesX, tilesY } = item.asset;
          const fcx = item.x + (tilesX - 1) / 2;
          const fcy = item.y + (tilesY - 1) / 2;
          const center = tileToScreen(fcx, fcy);
          const ax = center.x;
          const ay = center.y + TILE_H * (tilesX + tilesY) / 4;

          // Re-draw factory with lighter blend
          ctx.save();
          ctx.globalAlpha = pulse * 0.5;
          ctx.globalCompositeOperation = 'lighter';
          if (bgLayer.img.complete) ctx.drawImage(bgLayer.offscreen, ax - drawW / 2, ay - drawH, drawW, drawH);
          if (fgLayer.img.complete) ctx.drawImage(fgLayer.offscreen, ax - drawW / 2, ay - drawH, drawW, drawH);
          ctx.restore();
        }
      }

      // ── Draw deliver animations (particles, on top of everything) ─
      s.deliverAnims = s.deliverAnims.filter(a => {
        a.t += dt;
        if (a.t >= a.duration) return false;
        const { x: sx, y: sy } = tileToScreen(a.x, a.y);
        for (const p of a.particles) {
          const pt = Math.max(0, a.t - p.delay);
          if (pt <= 0) continue;
          const progress = pt / (a.duration - p.delay);
          const ease = 1 - (1 - progress) * (1 - progress);
          const alpha = 1 - ease;
          const px = sx + p.dx * ease;
          const py = sy - TILE_H * 1.6 + p.dy * ease;
          ctx.globalAlpha = alpha;
          ctx.beginPath();
          ctx.arc(px, py, p.size * (1 - ease * 0.5), 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.fill();
        }
        ctx.globalAlpha = 1;

        const textProgress = Math.min(a.t / a.duration, 1);
        const textEase = 1 - (1 - textProgress) * (1 - textProgress);
        ctx.globalAlpha = 1 - textEase;
        ctx.font = 'bold 16px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#10b981';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`+${a.count}`, sx, sy - TILE_H * 2.5 - 30 * textEase);
        ctx.textAlign = 'start';
        ctx.textBaseline = 'alphabetic';
        ctx.globalAlpha = 1;
        return true;
      });

      // ── Draw info points ──────────────────────────────────────────
      if (s.infoImage && s.infoImage.img.complete) {
        for (let i = 0; i < INFO_POINTS.length; i++) {
          const ip = INFO_POINTS[i];
          const { x: sx, y: sy } = tileToScreen(ip.x, ip.y);
          ctx.drawImage(s.infoImage.offscreen, sx - INFO_DRAW_W / 2, sy - INFO_DRAW_H + TILE_H / 4, INFO_DRAW_W, INFO_DRAW_H);

          // Tooltip on hover
          if (s.hoveredInfo === i) {
            const tipX = sx;
            const tipY = sy - INFO_DRAW_H - 4;
            const text = ip.text;
            ctx.font = '16px Inter, system-ui, sans-serif';

            // Word wrap
            const maxW = 300;
            const words = text.split(' ');
            const lines = [];
            let line = '';
            for (const word of words) {
              const test = line ? line + ' ' + word : word;
              if (ctx.measureText(test).width > maxW) {
                if (line) lines.push(line);
                line = word;
              } else {
                line = test;
              }
            }
            if (line) lines.push(line);

            const lineH = 24;
            const padX = 18, padY = 16;
            const boxW = maxW + padX * 2;
            const boxH = lines.length * lineH + padY * 2;

            // Bubble
            ctx.fillStyle = 'rgba(45,51,53,0.92)';
            ctx.beginPath();
            ctx.roundRect(tipX - boxW / 2, tipY - boxH, boxW, boxH, 6);
            ctx.fill();

            // Arrow
            ctx.beginPath();
            ctx.moveTo(tipX - 5, tipY);
            ctx.lineTo(tipX + 5, tipY);
            ctx.lineTo(tipX, tipY + 5);
            ctx.closePath();
            ctx.fill();

            // Text
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            for (let l = 0; l < lines.length; l++) {
              ctx.fillText(lines[l], tipX - boxW / 2 + padX, tipY - boxH + padY + l * lineH);
            }
            ctx.textAlign = 'start';
            ctx.textBaseline = 'alphabetic';
          }
        }
      }

      ctx.restore();

      // ── Debug overlay (screen space) ──────────────────────────────
      if (debugRef.current) {
        const hoverTile = s.hover ? `${s.hover.tx}, ${s.hover.ty}` : '—';
        const charTile = `${Math.round(s.char.x)}, ${Math.round(s.char.y)}`;
        const lines = [
          `char: (${charTile})`,
          `hover: (${hoverTile})`,
          `zoom: ${zoom.toFixed(2)}`,
          `cam: (${Math.round(s.camera.x)}, ${Math.round(s.camera.y)})`,
          `carried: ${s.carried}  delivered: ${s.delivered}`,
          `greenery: ${s.greeneryItems.length}  boxes: ${s.boxes.size}`,
        ];
        ctx.font = '12px monospace';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        const lh = 16;
        const bw = 220, bh = lines.length * lh + 16;
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillRect(12, H - bh - 12, bw, bh);
        ctx.fillStyle = '#00ff88';
        for (let i = 0; i < lines.length; i++) {
          ctx.fillText(lines[i], 20, H - bh - 4 + i * lh);
        }
        ctx.textBaseline = 'alphabetic';
      }

      animId = requestAnimationFrame(loop);
    };

    animId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const T = {
    primary: '#526075', fontBody: '"Inter", system-ui, sans-serif',
  };

  return (
    <Box sx={{ position: 'fixed', inset: 0, top: TESTBALK_TOP, zIndex: 1200, bgcolor: COLORS.bg }}>
      <Box sx={{ width: '100%', height: '100%' }}>
        <canvas
          ref={canvasRef}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          style={{ display: 'block', width: '100%', height: '100%', cursor: 'grab' }}
        />
      </Box>

      {/* HUD overlay */}
      <Box sx={{
        position: 'absolute', top: 16, left: 16, right: 16,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        pointerEvents: 'none',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pointerEvents: 'auto' }}>
          <Box
            onClick={() => navigate('/lab')}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              px: 2, py: 1, borderRadius: '0.375rem',
              bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)',
              fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem', color: T.primary,
              cursor: 'pointer', transition: 'all 0.15s', border: '1px solid rgba(45,51,53,0.08)',
              boxShadow: '0 1px 3px rgba(45,51,53,0.04)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_back</span>
            Lab
          </Box>
          <Box component="img" src="/assets/lab/Logo.svg" alt="Ahlstrom" sx={{ height: 28 }} />
        </Box>

        <Box sx={{
          pointerEvents: 'auto',
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2.5, py: 1, borderRadius: '0.375rem',
          bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)',
          border: '1px solid rgba(45,51,53,0.08)',
          boxShadow: '0 1px 3px rgba(45,51,53,0.04)',
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#7c3aed' }}>inventory_2</span>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 700, color: T.primary }}>
            {carried}
          </Typography>
          <Box sx={{ width: '1px', height: 14, bgcolor: 'rgba(45,51,53,0.12)' }} />
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#10b981' }}>warehouse</span>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 700, color: T.primary }}>
            {delivered}
          </Typography>
        </Box>
      </Box>

      {/* Start screen */}
      {!started && (
        <Box sx={{
          position: 'absolute', inset: 0,
          bgcolor: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10,
        }}>
          <Box sx={{
            bgcolor: '#fff', borderRadius: '0.5rem',
            boxShadow: '0 16px 48px rgba(45,51,53,0.12)',
            border: '1px solid rgba(45,51,53,0.08)',
            maxWidth: 420, width: '90%', p: 4, textAlign: 'center',
          }}>
            <Box component="img" src="/assets/lab/Logo.svg" alt="Ahlstrom" sx={{ height: 28, mb: 3 }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '1.1rem', fontWeight: 700, color: '#2d3335', mb: 1.5 }}>
              Welkom op het Ahlstrom terrein
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: '#767c7e', lineHeight: 1.7, mb: 3 }}>
              Loop over het terrein en verzamel de dozen.
              Breng ze naar de fabriek om ze af te leveren.
              Ontdek informatiepunten voor meer over Ahlstrom.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3, textAlign: 'left', px: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#7c3aed' }}>mouse</span>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: '#556071' }}>
                  Klik om te bewegen
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#7c3aed' }}>inventory_2</span>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: '#556071' }}>
                  Loop over dozen om ze op te pakken
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#10b981' }}>warehouse</span>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: '#556071' }}>
                  Loop naar de fabriek om af te leveren
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#5988A3' }}>zoom_in</span>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: '#556071' }}>
                  Scroll om te zoomen, sleep om te pannen
                </Typography>
              </Box>
            </Box>
            <Box
              onClick={() => { startedRef.current = true; setStarted(true); setShowUI(false); }}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1,
                px: 4, py: 1.5, borderRadius: '0.375rem', cursor: 'pointer',
                bgcolor: '#7c3aed', color: '#fff',
                fontFamily: T.fontBody, fontSize: '0.88rem', fontWeight: 700,
                transition: 'all 0.15s',
                '&:hover': { bgcolor: '#6d28d9', boxShadow: '0 4px 16px rgba(124,58,237,0.3)' },
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>play_arrow</span>
              Start
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
